/**
 * 規程チェックで使うパーツ画像と観点の定義。一覧と詳細の両方から参照する。
 *
 * 画像は src/assets 側に置き、ビルド時にハッシュ付きURLにする（public/ に同名で置くと
 * 差し替えてもブラウザ/CDNが古い画像を出し続けるため）。
 */
const ICONS = import.meta.glob('../assets/compliance/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

export const icon = (name: string) => ICONS[`../assets/compliance/${name}.png`] ?? '';

/** 判定シール。unjudged=要目視（条文が付かないもの）も同じテイストの印で揃える */
export const SEAL = {
  bad: icon('seal-bad'),
  caution: icon('seal-caution'),
  unjudged: icon('seal-unjudged'),
};

export const JUDGE_LABEL = {
  bad: '抵触の可能性',
  caution: '要確認',
  unjudged: '要目視',
} as const;

/** 観点14種：一覧では短縮名、ホバーで正式名と説明を出す */
export const CAT: Record<string, { icon: string; short: string; desc: string }> = {
  '人権・差別・不快表現': {
    icon: 'cat-human',
    short: '人権・差別',
    desc: '特定の人や集団を傷つける表現、差別的な決めつけ、侮辱や不快感を与える言い回し',
  },
  'プライバシー・肖像・名誉': {
    icon: 'cat-privacy',
    short: 'プライバシー',
    desc: '個人が特定される情報、無断の撮影・録音、名誉を損なう決めつけ',
  },
  '児童・青少年への配慮': {
    icon: 'cat-youth',
    short: '児童・青少年',
    desc: '子どもが見る時間帯の表現、模倣の危険、いじめを想起させる扱い',
  },
  '暴力・残虐表現': {
    icon: 'cat-violence',
    short: '暴力・残虐',
    desc: '暴力行為や残虐な描写、強い恐怖を与える映像',
  },
  性表現: { icon: 'cat-sex', short: '性表現', desc: '性的な描写・示唆、身体への過度な注目' },
  '犯罪・模倣誘発': {
    icon: 'cat-crime',
    short: '犯罪・模倣',
    desc: '犯罪の手口、危険行為の再現、まねを誘う見せ方',
  },
  '医療・健康・科学的根拠': {
    icon: 'cat-medical',
    short: '医療・健康',
    desc: '効果効能の断定、根拠の乏しい健康情報、医療行為の扱い',
  },
  '消費者保護・誇大/不当表示': {
    icon: 'cat-consumer',
    short: '消費者保護',
    desc: '価格や効果の誇張、条件の省略、優良誤認のおそれ',
  },
  '広告・商標・ステルスマーケティング': {
    icon: 'cat-ad',
    short: '広告・商標',
    desc: '特定の商品・企業の宣伝的な扱い、広告と番組の区別',
  },
  '政治的公平・選挙': {
    icon: 'cat-politics',
    short: '政治的公平',
    desc: '政治的な主張の扱い、選挙期間中の公平性',
  },
  '報道の正確性・誤解誘導': {
    icon: 'cat-news',
    short: '報道の正確性',
    desc: '事実の断定、裏取りの要否、編集による誤解の誘発',
  },
  '引用・著作権': {
    icon: 'cat-copyright',
    short: '引用・著作権',
    desc: '第三者の著作物の利用、引用の範囲、クレジット表記',
  },
  '災害・緊急時報道': {
    icon: 'cat-disaster',
    short: '災害・緊急',
    desc: '災害時の表現、不安をあおる言い回し、避難情報の扱い',
  },
  'その他・一般': {
    icon: 'cat-other',
    short: 'その他',
    desc: '上記に当てはまらない、番組全体としての配慮事項',
  },
};

const FALLBACK_CAT = CAT['その他・一般'];
export const catOf = (c?: string) => (c && CAT[c]) || FALLBACK_CAT;
export const catIconSrc = (c?: string) => icon(catOf(c).icon);
